import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  Collapse,
} from '@mui/material';
import { ChevronDownIcon, ChevronUpIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import AppIconButton from '../ui/AppIconButton';

const ONBOARDING_KEY = 'finance_onboarding_completed';

interface OnboardingWizardProps {
  onComplete: () => void;
}

interface StepConfig {
  icon: string;
  title: string;
  description: string;
  actionLabel: string;
  path: string;
}

const STEPS: StepConfig[] = [
  {
    icon: '\uD83C\uDFE6',
    title: 'Import Your Bank Statement',
    description:
      'Upload a CSV or Excel export from your bank to automatically pull in all transactions.',
    actionLabel: 'Import Now',
    path: '/vendor/finance/import',
  },
  {
    icon: '\uD83C\uDFF7\uFE0F',
    title: 'Categorize Transactions',
    description:
      'Set up expense categories that match your restaurant operations.',
    actionLabel: 'Review Categories',
    path: '/vendor/finance/settings',
  },
  {
    icon: '\uD83D\uDCC8',
    title: 'See Your P&L',
    description:
      'View income vs expenses, food cost %, and supplier payables in one dashboard.',
    actionLabel: 'View Dashboard',
    path: '/vendor/finance',
  },
];

const OnboardingWizard: React.FC<OnboardingWizardProps> = ({ onComplete }) => {
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);

  const handleDismiss = () => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    onComplete();
  };

  const handleAction = (path: string) => {
    localStorage.setItem(ONBOARDING_KEY, 'true');
    onComplete();
    navigate(path);
  };

  return (
    <Paper
      sx={{
        borderRadius: '12px',
        backgroundColor: `${theme.primary}08`,
        border: `1px solid ${theme.primary}33`,
        overflow: 'hidden',
        mb: { xs: 2, sm: 3 },
      }}
      elevation={0}
    >
      {/* Collapsed banner header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.5,
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography sx={{ fontSize: '1.25rem', lineHeight: 1 }}>
            {'\uD83D\uDE80'}
          </Typography>
          <Box>
            <Typography
              sx={{
                fontWeight: 700,
                color: theme.text,
                fontSize: '0.875rem',
                lineHeight: 1.3,
              }}
            >
              Get started with Finance
            </Typography>
            <Typography
              variant="caption"
              sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.75rem' }}
            >
              3 steps to set up your financial tracking
            </Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AppIconButton
            icon={<XMarkIcon style={{ width: 18, height: 18 }} />}
            tooltip="Dismiss"
            variant="secondary"
            size="small"
            onClick={(e) => { e.stopPropagation(); handleDismiss(); }}
            data-testid="onboarding-dismiss"
          />
          {expanded
            ? <ChevronUpIcon style={{ width: 18, height: 18, color: theme.gray?.[400] }} />
            : <ChevronDownIcon style={{ width: 18, height: 18, color: theme.gray?.[400] }} />
          }
        </Box>
      </Box>

      {/* Expandable step cards */}
      <Collapse in={expanded}>
        <Box sx={{ px: 2, pb: 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {STEPS.map((step, i) => (
            <Box
              key={i}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                p: 1.5,
                borderRadius: '10px',
                backgroundColor: theme.components?.card?.background || '#FFFFFF',
                border: `1px solid ${theme.components?.card?.border || '#E0E0E0'}`,
              }}
            >
              <Typography sx={{ fontSize: '1.5rem', lineHeight: 1, flexShrink: 0 }}>
                {step.icon}
              </Typography>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                  sx={{ fontWeight: 600, color: theme.text, fontSize: '0.813rem', lineHeight: 1.3 }}
                >
                  {step.title}
                </Typography>
                <Typography
                  variant="caption"
                  sx={{ color: theme.gray?.[500] || '#666', fontSize: '0.7rem', lineHeight: 1.4 }}
                >
                  {step.description}
                </Typography>
              </Box>
              <Button
                size="small"
                onClick={() => handleAction(step.path)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  color: theme.primary,
                  borderRadius: '8px',
                  minWidth: 'auto',
                  minHeight: 44,
                  whiteSpace: 'nowrap',
                  '&:hover': { backgroundColor: `${theme.primary}11` },
                  '&:focus-visible': { outline: '2px solid var(--focus-ring)', outlineOffset: '2px' },
                }}
              >
                {step.actionLabel}
              </Button>
            </Box>
          ))}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default OnboardingWizard;
